<template>
  <div class="max-w-4xl mx-auto space-y-6">
    <!-- Wallet Selection Card -->
    <div v-if="`${authStore.user.is_active}` ==='1' && !dataStore.shouldHideWallet" class="bg-background-light rounded-lg shadow-sm">
      <div class="px-6 py-4 border-b border-background-lighter">
        <h2 class="text-xl font-semibold text-text-primary">Wallet Selection</h2>
        <p class="text-sm text-text-secondary mt-1">Choose your active wallet for transactions</p>
      </div>

      <div class="p-6">
        <div class="space-y-3">
          <!-- USD Wallet -->
          <div class="flex items-center p-3 border border-background-lighter rounded-lg hover:bg-background-lighter transition-colors">
            <div class="flex items-center">
              <input
                  type="radio"
                  id="wallet_usd"
                  name="active_wallet"
                  value="0"
                  v-model="selectedWallet"
                  @change="updateActiveWallet"
                  :disabled="isUpdatingWallet"
                  class="h-4 w-4 text-primary focus:ring-primary border-background-lighter"
              />
              <label for="wallet_usd" class="ml-3 flex items-center cursor-pointer">
                <span class="text-sm font-medium text-text-primary">US Dollar Wallet</span>
                <span :class="[
                  'ml-2 px-2 py-1 text-xs font-medium rounded-full',
                  selectedWallet === '0' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                ]">
                  {{ formatCurrency(walletBalances.usd || 0, 'USD') }}
                </span>
              </label>
            </div>
          </div>

          <!-- EUR Wallet -->
          <div class="flex items-center p-3 border border-background-lighter rounded-lg hover:bg-background-lighter transition-colors">
            <div class="flex items-center">
              <input
                  type="radio"
                  id="wallet_euro"
                  name="active_wallet"
                  value="1"
                  v-model="selectedWallet"
                  @change="updateActiveWallet"
                  :disabled="isUpdatingWallet"
                  class="h-4 w-4 text-primary focus:ring-primary border-background-lighter"
              />
              <label for="wallet_euro" class="ml-3 flex items-center cursor-pointer">
                <span class="text-sm font-medium text-text-primary">Euro Wallet</span>
                <span :class="[
                  'ml-2 px-2 py-1 text-xs font-medium rounded-full',
                  selectedWallet === '1' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                ]">
                  {{ formatCurrency(walletBalances.eur || 0, 'EUR') }}
                </span>
              </label>
            </div>
          </div>

          <!-- DZD Wallet -->
          <div class="flex items-center p-3 border border-background-lighter rounded-lg hover:bg-background-lighter transition-colors">
            <div class="flex items-center">
              <input
                  type="radio"
                  id="wallet_dzd"
                  name="active_wallet"
                  value="2"
                  v-model="selectedWallet"
                  @change="updateActiveWallet"
                  :disabled="isUpdatingWallet"
                  class="h-4 w-4 text-primary focus:ring-primary border-background-lighter"
              />
              <label for="wallet_dzd" class="ml-3 flex items-center cursor-pointer">
                <span class="text-sm font-medium text-text-primary">Algerian Dinar Wallet</span>
                <span :class="[
                  'ml-2 px-2 py-1 text-xs font-medium rounded-full',
                  selectedWallet === '2' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                ]">
                  {{ formatCurrency(walletBalances.dzd || 0, 'DZD') }}
                </span>
              </label>
            </div>
          </div>

          <!-- EGP Wallet -->
          <div class="flex items-center p-3 border border-background-lighter rounded-lg hover:bg-background-lighter transition-colors">
            <div class="flex items-center">
              <input
                  type="radio"
                  id="wallet_egp"
                  name="active_wallet"
                  value="3"
                  v-model="selectedWallet"
                  @change="updateActiveWallet"
                  :disabled="isUpdatingWallet"
                  class="h-4 w-4 text-primary focus:ring-primary border-background-lighter"
              />
              <label for="wallet_egp" class="ml-3 flex items-center cursor-pointer">
                <span class="text-sm font-medium text-text-primary">Egyptian Pound Wallet</span>
                <span :class="[
                  'ml-2 px-2 py-1 text-xs font-medium rounded-full',
                  selectedWallet === '3' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                ]">
                  {{ formatCurrency(walletBalances.egp || 0, 'EGP') }}
                </span>
              </label>
            </div>
          </div>

          <!-- Flexy Wallet (Read-only) -->
          <div v-if="authStore.user.is_flexy === '1'" class="flex items-center p-3 border border-background-lighter rounded-lg bg-background-lighter">
            <div class="flex items-center opacity-75">
              <input
                  type="radio"
                  id="wallet_flexy"
                  name="active_wallet"
                  disabled
                  class="h-4 w-4 text-text-secondary border-background-lighter"
              />
              <label for="wallet_flexy" class="ml-3 flex items-center">
                <span class="text-sm font-medium text-text-secondary">Flexy Wallet (Read-only)</span>
                <span class="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                  {{ formatCurrency(walletBalances.flexy || 0, 'DZD') }}
                </span>
              </label>
            </div>
          </div>
        </div>

        <!-- Update Status -->
        <div v-if="isUpdatingWallet" class="mt-4 flex items-center justify-center">
          <div class="animate-spin h-5 w-5 text-primary">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <span class="ml-2 text-sm text-text-secondary">Updating wallet selection...</span>
        </div>
      </div>
    </div>

    <!-- Profile Information Card -->
    <div class="bg-background-light rounded-lg shadow-sm">
      <div class="px-6 py-4 border-b border-background-lighter">
        <h2 class="text-xl font-semibold text-text-primary">Profile Information </h2>
        <p class="text-sm text-text-secondary mt-1">Update your personal information</p>
      </div>

      <div class="p-6">
        <form @submit.prevent="updateProfile" class="space-y-6">
          <!-- Profile Picture Display -->
          <div class="flex items-center space-x-6">
            <div class="w-20 h-20 bg-primary rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {{ userInitials }}
            </div>
            <div>
              <h3 class="text-lg font-medium text-text-primary">{{ profileForm.name || 'User' }}</h3>
              <p class="text-text-secondary">{{ authStore.user?.email }}</p>
              <p v-if="authStore.user?.is_active" class="text-sm text-primary mt-1">
                Active Wallet: {{ getActiveWalletName() }}
              </p>
            </div>
          </div>

          <!-- Form Fields -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Full Name -->
            <div>
              <label for="name" class="block text-sm font-medium text-text-secondary mb-2">
                Full Name <span class="text-red-500">*</span>
              </label>
              <input
                  id="name"
                  v-model="profileForm.name"
                  type="text"
                  :disabled="isUpdatingProfile"
                  class="w-full px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary disabled:bg-background disabled:cursor-not-allowed"
                  placeholder="Enter your full name"
                  required
              />
              <div v-if="errors.name" class="mt-1 text-sm text-red-600">
                {{ errors.name[0] }}
              </div>
            </div>

            <!-- Phone Number -->
            <div>
              <label for="phone" class="block text-sm font-medium text-text-secondary mb-2">
                Phone Number
              </label>
              <input
                  id="phone"
                  v-model="profileForm.phone"
                  type="tel"
                  :disabled="isUpdatingProfile"
                  class="w-full px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary disabled:bg-background disabled:cursor-not-allowed"
                  placeholder="Enter your phone number"
              />
              <div v-if="errors.phone" class="mt-1 text-sm text-red-600">
                {{ errors.phone[0] }}
              </div>
            </div>
          </div>

          <!-- Update Button -->
          <div class="flex justify-end pt-4">
            <button
                type="submit"
                :disabled="isUpdatingProfile || !hasProfileChanges"
                class="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <div v-if="isUpdatingProfile" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              {{ isUpdatingProfile ? 'Updating...' : 'Update Profile' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Change Password Card -->
    <div class="bg-background-light rounded-lg shadow-sm">
      <div class="px-6 py-4 border-b border-background-lighter">
        <h2 class="text-xl font-semibold text-text-primary">Change Password</h2>
        <p class="text-sm text-text-secondary mt-1">Update your password to keep your account secure</p>
      </div>

      <div class="p-6">
        <form @submit.prevent="updatePassword" class="space-y-6">
          <!-- Current Password -->
          <div>
            <label for="current_password" class="block text-sm font-medium text-text-secondary mb-2">
              Current Password <span class="text-red-500">*</span>
            </label>
            <input
                id="current_password"
                v-model="passwordForm.current_password"
                type="password"
                :disabled="isUpdatingPassword"
                class="w-full px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary disabled:bg-background disabled:cursor-not-allowed"
                placeholder="Enter your current password"
                required
            />
            <div v-if="errors.current_password" class="mt-1 text-sm text-red-600">
              {{ errors.current_password[0] }}
            </div>
          </div>

          <!-- New Password -->
          <div>
            <label for="new_password" class="block text-sm font-medium text-text-secondary mb-2">
              New Password <span class="text-red-500">*</span>
            </label>
            <input
                id="new_password"
                v-model="passwordForm.new_password"
                type="password"
                :disabled="isUpdatingPassword"
                class="w-full px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary disabled:bg-background disabled:cursor-not-allowed"
                placeholder="Enter your new password"
                required
                minlength="8"
            />
            <div v-if="errors.new_password" class="mt-1 text-sm text-red-600">
              {{ errors.new_password[0] }}
            </div>
          </div>

          <!-- Confirm New Password -->
          <div>
            <label for="new_password_confirmation" class="block text-sm font-medium text-text-secondary mb-2">
              Confirm New Password <span class="text-red-500">*</span>
            </label>
            <input
                id="new_password_confirmation"
                v-model="passwordForm.new_password_confirmation"
                type="password"
                :disabled="isUpdatingPassword"
                class="w-full px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary disabled:bg-background disabled:cursor-not-allowed"
                placeholder="Confirm your new password"
                required
                minlength="8"
            />
            <div v-if="errors.new_password_confirmation" class="mt-1 text-sm text-red-600">
              {{ errors.new_password_confirmation[0] }}
            </div>
          </div>

          <!-- Update Button -->
          <div class="flex justify-end pt-4">
            <button
                type="submit"
                :disabled="isUpdatingPassword || !hasPasswordChanges"
                class="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <div v-if="isUpdatingPassword" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              {{ isUpdatingPassword ? 'Updating...' : 'Change Password' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useAuthStore } from '@/stores/auth.js';
import { useDataStore } from '@/stores/dataStore.js'

import api from '../../services/api';
import Swal from 'sweetalert2';

export default {
  name: 'Profile',
  setup() {
    const authStore = useAuthStore();
    const dataStore = useDataStore();

    const profileForm = ref({
      name: '',
      phone: ''
    });

    const passwordForm = ref({
      current_password: '',
      new_password: '',
      new_password_confirmation: ''
    });

    // Wallet related states
    const selectedWallet = ref('0'); // Default to USD (is_da = 0)
    const isUpdatingWallet = ref(false);
    const walletBalances = ref({
      usd: 0,
      eur: 0,
      dzd: 0,
      egp: 0,
      flexy: 0
    });

    const isUpdatingProfile = ref(false);
    const isUpdatingPassword = ref(false);
    const errors = ref({});

    // Original data for comparison
    const originalProfileData = ref({});

    const userInitials = computed(() => {
      const name = profileForm.value.name || authStore.user?.name || 'U';
      return name.charAt(0).toUpperCase();
    });

    // Check if profile form has changes
    const hasProfileChanges = computed(() => {
      return profileForm.value.name !== originalProfileData.value.name ||
          profileForm.value.phone !== originalProfileData.value.phone;
    });

    // Check if password form has all required fields
    const hasPasswordChanges = computed(() => {
      return passwordForm.value.current_password &&
          passwordForm.value.new_password &&
          passwordForm.value.new_password_confirmation &&
          passwordForm.value.new_password === passwordForm.value.new_password_confirmation;
    });

    const formatCurrency = (amount, currency) => {
      const formattedAmount = parseFloat(amount || 0).toFixed(2);
      const symbols = {
        'USD': '$',
        'EUR': '€',
        'DZD': 'DZD',
        'EGP': 'EGP'
      };
      return `${formattedAmount} ${symbols[currency] || currency}`;
    };

    const getActiveWalletName = () => {
      const walletNames = {
        '0': 'US Dollar',
        '1': 'Euro',
        '2': 'Algerian Dinar',
        '3': 'Egyptian Pound'
      };
      return walletNames[selectedWallet.value] || 'Unknown';
    };

    const loadUserData = () => {
      if (authStore.user) {
        const userData = {
          name: authStore.user.name || '',
          phone: authStore.user.phone || ''
        };

        profileForm.value = { ...userData };
        originalProfileData.value = { ...userData };

        // Load wallet data
        selectedWallet.value = String(authStore.user.is_da || 0);
        walletBalances.value = {
          usd: authStore.user.wallet || 0,
          eur: authStore.user.wallet_euro || 0,
          dzd: authStore.user.wallet_da || 0,
          egp: authStore.user.wallet_egp || 0,
          flexy: authStore.user.wallet_flexy || 0
        };
      }
    };

    const clearErrors = () => {
      errors.value = {};
    };

    const updateActiveWallet = async () => {
      if (isUpdatingWallet.value) return;

      isUpdatingWallet.value = true;

      try {
        const response = await api.put('/user/wallet', {
          is_da: parseInt(selectedWallet.value)
        });

        if (response.data.success) {
          // Update the auth store
          const updatedUser = { ...authStore.user, is_da: parseInt(selectedWallet.value) };
          authStore.user = updatedUser;
          dataStore.updateUserCurrency(parseInt(selectedWallet.value));



          localStorage.setItem('user', JSON.stringify(updatedUser));

          // Show success message
          Swal.fire({
            title: 'Success!',
            text: `Active wallet changed to ${getActiveWalletName()}.`,
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
            toast: true,
            position: 'top-end'
          });
        }

      } catch (error) {
        console.error('Wallet update error:', error);
        Swal.fire({
          title: 'Error!',
          text: error.response?.data?.message || 'Failed to update active wallet.',
          icon: 'error'
        });

        // Revert the selection
        selectedWallet.value = String(authStore.user?.is_da || 0);
      } finally {
        isUpdatingWallet.value = false;
      }
    };

    const updateProfile = async () => {
      if (isUpdatingProfile.value) return;

      clearErrors();
      isUpdatingProfile.value = true;

      try {
        const response = await api.put('/user/profile', {
          name: profileForm.value.name,
          phone: profileForm.value.phone
        });

        if (response.data.success) {
          // Update the auth store with new user data
          const updatedUser = { ...authStore.user, ...response.data.user };
          authStore.user = updatedUser;
          localStorage.setItem('user', JSON.stringify(updatedUser));
          // Update original data
          originalProfileData.value = { ...profileForm.value };

          // Show success message
          Swal.fire({
            title: 'Success!',
            text: 'Profile updated successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
            toast: true,
            position: 'top-end'
          });
        }

      } catch (error) {
        console.error('Profile update error:', error);

        if (error.response?.status === 422 && error.response?.data?.errors) {
          errors.value = error.response.data.errors;
        } else {
          Swal.fire({
            title: 'Error!',
            text: error.response?.data?.message || 'Failed to update profile.',
            icon: 'error'
          });
        }
      } finally {
        isUpdatingProfile.value = false;
      }
    };

    const updatePassword = async () => {
      if (isUpdatingPassword.value) return;

      // Validate password confirmation
      if (passwordForm.value.new_password !== passwordForm.value.new_password_confirmation) {
        Swal.fire({
          title: 'Error!',
          text: 'New password and confirmation do not match.',
          icon: 'error'
        });
        return;
      }

      clearErrors();
      isUpdatingPassword.value = true;

      try {
        const response = await api.put('/user/password', {
          current_password: passwordForm.value.current_password,
          password: passwordForm.value.new_password,
          password_confirmation: passwordForm.value.new_password_confirmation
        });

        if (response.data.success) {
          // Clear the form
          passwordForm.value = {
            current_password: '',
            new_password: '',
            new_password_confirmation: ''
          };

          // Show success message and redirect to login
          await Swal.fire({
            title: 'Success!',
            text: 'Password updated successfully. Please login again with your new password.',
            icon: 'success',
            confirmButtonText: 'OK'
          });

          // Logout user since token was revoked
          await authStore.logout();
          window.location.href = '/login';
        }

      } catch (error) {
        console.error('Password update error:', error);

        if (error.response?.status === 422 && error.response?.data?.errors) {
          errors.value = error.response.data.errors;
        } else {
          Swal.fire({
            title: 'Error!',
            text: error.response?.data?.message || 'Failed to update password.',
            icon: 'error'
          });
        }
      } finally {
        isUpdatingPassword.value = false;
      }
    };

    // Watch for user changes
    watch(() => authStore.user, (newUser) => {
      if (newUser) {
        loadUserData();
      }
    }, { immediate: true });

    onMounted(() => {
      loadUserData();
    });

    return {
      profileForm,
      passwordForm,
      selectedWallet,
      isUpdatingWallet,
      walletBalances,
      isUpdatingProfile,
      isUpdatingPassword,
      errors,
      authStore,
      dataStore,
      userInitials,
      hasProfileChanges,
      hasPasswordChanges,
      formatCurrency,
      getActiveWalletName,
      updateActiveWallet,
      updateProfile,
      updatePassword
    };
  }
}
</script>

<style scoped>
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
:root {
  --color-background: #ffffff;
  --color-background-light: #f8fafc;
  --color-background-lighter: #f1f5f9;
  --color-text-primary: #0f172a;
  --color-text-secondary: #64748b;
  --color-primary: #3b82f6;
  --color-primary-dark: #2563eb;
}

:root.dark {
  --color-background: #0f172a;
  --color-background-light: #1e293b;
  --color-background-lighter: #334155;
  --color-text-primary: #f8fafc;
  --color-text-secondary: #cbd5e1;
  --color-primary: #60a5fa;
  --color-primary-dark: #3b82f6;
}

</style>
